import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useApi from 'hooks/useApi'
import {
  WppActionButton,
  WppBreadcrumb,
  WppButton,
  WppSkeleton,
  WppTypography,
} from '@wppopen/components-library-react'
import Banner from 'components/Banner'
import NotFound404Page from 'pages/NotFound404Page/NotFound404Page'
import Table from 'components/table'
import appContext from 'context/appContext'
import { columnStructureBrands } from './utils'
import { getFormattedDate } from 'utils'
import usePermissions from 'hooks/usePermissions'
import TableMetaDataRow from 'components/table/TableMetaDataRow'
import PageHeader from 'components/PageHeader/PageHeader'
import useInitialiser from 'hooks/useInitialiser'
import ImportEditClientModal from 'components/Modals/ImportEditClientModal/ImportEditClientModal'
import SettingsPageContext from 'context/settingsPageContext/settingsPageContext'
import { BANNER_KEYS, PDP_GENERATOR_API_HEADERS, unknown } from 'utils/constants'
import { GenericModal } from 'components/Modals'
import useCurrentUser from 'hooks/useCurrentUser'
import useToast from 'hooks/useToast'
import type { OnSubmitGenericModalProps } from 'components/Modals/GenericModal/helper'
import type { FEApiError } from 'utils/errorHandling'
import type { ArchiveCreateData, ClientsCreatePayload, ClientsDetailData, ClientsUpdateData } from '@contracts'
import navigationHeaderStyles from '../../NavigationHeader.module.scss'
import styles from './Clients.module.scss'

export default function ClientView() {
  const [isDeleteClient, setIsDeleteClient] = useState(false)
  const [isEditClient, setIsEditClient] = useState(false)
  const { importedClients, fetchClientData, isClientApiLoading } = useContext(appContext)
  const navigate = useNavigate()
  const { resetBanner, toastDetailsContext, setToastDetailsContext, activeBannerId, setActiveBannerId } =
    useContext(SettingsPageContext)
  const { toastDetails, setToastDetails } = useToast()
  const { client_id } = useParams()
  const { userName } = useCurrentUser()
  const clientBrands = useMemo(() => importedClients?.find(({ id }) => id === client_id)?.brands, [importedClients])

  const getClient = useApi<ClientsDetailData>(`clients/${client_id}`)
  const {
    fetchData: getSingleClient,
    data: getSingleClientData,
    error: getSingleClientError,
    url: getSingleClientUrl,
  } = getClient

  const {
    fetchData: deleteSingleClient,
    isLoading: isDeleteSingleClientLoading,
    url: deleteSingleClientUrl,
  } = useApi<ArchiveCreateData>(`clients/${client_id}/archive`)

  const { fetchData: putSingleClient, url: putSingleClientUrl } = useApi<ClientsUpdateData>(`clients/${client_id}`)

  const { clientLevelPermissions, isGetPermissionsLoading } = usePermissions({
    clientId: client_id,
    permissionSubset: [
      'PDP_APP_PUT_CLIENTS_BY',
      'PDP_APP_GET_CLIENTS_BY',
      'PDP_APP_POST_CLIENTS_BY_ARCHIVE',
      'PDP_APP_POST_BRANDS',
    ],
  })
  const [canPutClient, canGetSingleClient, canDeleteClient, canPostBrand] = clientLevelPermissions
  const { isInitialising: isApisInitialising } = useInitialiser({ subscribedApis: [getClient] })
  const isInitialising = isApisInitialising || isGetPermissionsLoading

  // fetch single client data
  useEffect(() => {
    getSingleClient(getSingleClientUrl, {
      headers: { [PDP_GENERATOR_API_HEADERS.CLIENT_IDS_HEADER]: client_id as string },
    }).catch(err => console.error(err))
  }, [])

  const menuItems = useMemo(() => {
    const items = []

    if (canPutClient) {
      items.push({
        label: 'Edit client',
        onClick: () => setIsEditClient(true),
      })
    }

    if (canDeleteClient) {
      items.push({
        label: 'Delete client',
        onClick: () => setIsDeleteClient(true),
      })
    }

    return items
  }, [canPutClient, canDeleteClient])

  if (
    !isInitialising &&
    (!canGetSingleClient || getSingleClientError?.status === 404 || getSingleClientError?.status === 500)
  )
    return <NotFound404Page />

  const onDeleteClient = async ({ comment }: OnSubmitGenericModalProps) => {
    const body = {
      update_info: {
        user_name: userName,
        comment,
      },
    }

    try {
      if (!canDeleteClient) throw new Error('Not allowed to delete client')
      await deleteSingleClient(deleteSingleClientUrl, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { [PDP_GENERATOR_API_HEADERS.CLIENT_IDS_HEADER]: client_id as string },
      })

      await fetchClientData()

      setActiveBannerId(BANNER_KEYS.SETTINGS_HOME)
      setToastDetailsContext({
        type: 'success',
        message: 'Client deleted successfully',
      })

      navigate('/settings/clients')
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  const onEditClient = async (data: ClientsCreatePayload) => {
    const { id, ...rest } = data
    try {
      if (!canPutClient) throw new Error('Not allowed to edit client')
      await putSingleClient(putSingleClientUrl, {
        method: 'PUT',
        body: JSON.stringify(rest),
      })

      await getSingleClient(getSingleClientUrl, {
        headers: { [PDP_GENERATOR_API_HEADERS.CLIENT_IDS_HEADER]: client_id as string },
      })

      setToastDetails({
        type: 'success',
        message: 'Client updated successfully',
      })

      setIsEditClient(false)
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  return (
    <div className="dark-bg-page">
      <section className="light-header">
        <div className={navigationHeaderStyles.navigationHeader__container}>
          {isInitialising ? (
            <WppSkeleton width={200} height={18} />
          ) : (
            <WppBreadcrumb
              items={[
                {
                  label: 'Settings',
                  path: '/settings/clients',
                },
                {
                  label: getSingleClientData?.client_name || 'Client',
                  path: '',
                },
              ]}
              middleTruncation
              onWppChange={e => {
                if (isInitialising) return
                const { path } = e.detail

                navigate(path)
              }}
            />
          )}

          <Banner
            text={toastDetailsContext?.message || toastDetails?.message}
            type={toastDetailsContext?.type || toastDetails?.type}
            shouldBannerShow={(activeBannerId === client_id && !!toastDetailsContext) || !!toastDetails}
            onClose={() => {
              setToastDetails(undefined)
              resetBanner()
            }}
          />
        </div>

        <PageHeader
          isLoading={isInitialising}
          tags={[]}
          createdDetails={`${getSingleClientData?.updated_at === getSingleClientData?.created_at ? 'Created' : 'Updated'}: ${getFormattedDate(getSingleClientData?.updated_at)} by ${getSingleClientData?.update_info?.user_name || unknown}`}
          pageTitle={getSingleClientData?.client_name!}
          menuItems={menuItems}
          className={styles.pageHeader}
          image={getSingleClientData?.client_logo_url}
          details={{
            'Content requiring approvals':
              [
                getSingleClientData?.approval_required?.regional_standard_generation ? 'Regional standard content' : '',
                getSingleClientData?.approval_required?.retailer_adaption ? 'Retailer content' : '',
                getSingleClientData?.approval_required?.sku_adaption ? 'SKU content' : '',
              ]
                .filter(Boolean)
                .join(' | ') || '-',
          }}
        />
      </section>
      <section className="page-content">
        <div className={styles.tableContainer}>
          <TableMetaDataRow
            text={`Showing ${clientBrands?.length || 0} results`}
            refreshHandler={() => {
              fetchClientData()
            }}
            isInitialising={isInitialising}
            permissions={{
              canRefresh: !!clientBrands?.length,
            }}
          >
            {canPostBrand && (
              <WppButton
                onClick={() => {
                  navigate(`/create/${client_id}/brand`)
                }}
              >
                Add brand
              </WppButton>
            )}
          </TableMetaDataRow>

          <Table
            theme="tertiary"
            rowData={clientBrands}
            columnDefs={columnStructureBrands}
            onRowClicked={({ data }) => {
              if (isInitialising) {
                return
              }
              navigate(`brand/${data.id}`)
            }}
            isDrillable
            isInitialising={isInitialising}
            loadedRows={clientBrands?.length || 0}
            totalRows={clientBrands?.length || 0}
            loading={isClientApiLoading}
            noRowsOverlayComponent={() => (
              <WppTypography tag="p" type="s-body">
                You haven`t added any brands yet,
                <span>
                  <WppActionButton onClick={() => navigate(`/create/${client_id}/brand`)} className={styles.upload}>
                    click to add
                  </WppActionButton>
                </span>
              </WppTypography>
            )}
          />
        </div>
      </section>

      {isDeleteClient && (
        <GenericModal
          onSubmit={onDeleteClient}
          onCancel={() => setIsDeleteClient(false)}
          modalTitle="Delete client"
          submitButtonText="Delete"
          isApiLoading={isDeleteSingleClientLoading || isClientApiLoading}
          shouldModalOpen={isDeleteClient}
          fieldsConfig={{
            hasCommentField: true,
            isCommentRequiredField: false,
          }}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            This action will impact <b>{getSingleClientData?.metadata?.project_count} projects</b> and{' '}
            <b>{getSingleClientData?.metadata?.job_count} tasks</b>.<br />
            After you delete a client this action cannot be undone.
          </WppTypography>
        </GenericModal>
      )}

      {isEditClient && (
        <ImportEditClientModal
          onCancel={() => setIsEditClient(false)}
          isModalOpen={isEditClient}
          isApiLoading={isInitialising}
          clientData={getSingleClientData!}
          onSubmit={onEditClient}
        />
      )}
    </div>
  )
}
